/**
 * Automation (workflow) definitions: a typed, acyclic node graph that PRISM
 * interprets on Temporal. Versions are immutable once published; every run is
 * pinned to one version.
 *
 * A graph has exactly one trigger node. Edges connect an output port of one
 * node to another node. Condition nodes have `true` / `false` ports; every
 * other node has a single `next` port. Several edges from one port run their
 * targets in parallel; a node with several incoming edges waits for all of
 * its incoming branches that are taken (a join).
 */
import { z } from 'zod';
import { EXPORT_FORMAT_IDS, EXPORT_PRESET_IDS } from './graphics.ts';
import { QcTaskAssigneeSchema } from './qc.ts';
import { DEFAULT_SCHEDULE_TIMEZONE, isTimeZone } from './date-schedule.ts';
import { DeliveryItemTemplate } from './portal.ts';
import { GATEWAY_METHODS } from './gateway.ts';
import { SidecarFormatsSchema } from './subtitles.ts';
import { FOR_EACH_LIMITS, MEDIA_LINK_TTL, forEachBodies, isLinkTemplateUsed } from './automation-eval.ts';
import { FORMAT_CLASSES, MasterChoiceOrAllSchema, MasterChoiceSchema, MASTER_ADDED_EVENT, type MasterChoice, type MasterChoiceOrAll } from './masters.ts';

export const AUTOMATION_SCHEMA_VERSION = 1;

/* ---------------------------------------------------------------- conditions */

/** "Asset has master [variant] [format]" (1.2.0): a variant (null: any) and a format class (null: any). */
export const MasterMatchSchema = z.object({
  variantId: z.string().uuid().nullable().default(null),
  format: z.enum(FORMAT_CLASSES).nullable().default(null),
});
export type MasterMatch = z.infer<typeof MasterMatchSchema>;

export const ConditionSchema = z.object({
  /**
   * A path into the run context, e.g. asset.metadata.f_rights or nodes.inspect.output.durationSeconds. For has_master
   * and no_master (1.2.0) the path is asset.masters (the current version's masters in the run context).
   */
  path: z.string().min(1).max(200),
  op: z.enum(['equals', 'not_equals', 'contains', 'greater_than', 'less_than', 'exists', 'not_exists', 'has_master', 'no_master']),
  value: z.union([z.string(), z.number(), z.boolean()]).optional(),
  /** has_master / no_master: which master (absent: any master at all). */
  master: MasterMatchSchema.optional(),
});
export type Condition = z.infer<typeof ConditionSchema>;

/* ---------------------------------------------------------------- dates (1.0.0-rc.10) */

/**
 * A date or date-time field ± days at a time of day in a time zone (see date-schedule.ts). Shared by the "Date in
 * metadata" trigger, the "Wait until a date" step and the "Create task" step's due time.
 */
export const DateRuleShape = {
  /** A date or date-time field of the metadata model. */
  fieldId: z.string().regex(/^f_[a-z0-9_]+$/),
  offsetDays: z.number().int().min(-3650).max(3650).default(0),
  /** HH:MM local time. */
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).default('09:00'),
  /** A date-time field fires at its own time of day instead of `time`. */
  useFieldTime: z.boolean().default(false),
  timezone: z.string().min(1).max(64).default(DEFAULT_SCHEDULE_TIMEZONE),
};

/* ---------------------------------------------------------------- triggers */

/** 1.3.0: the domain events Intake emits when it accepts a portal submission (payload: IntakeAcceptedPayload). */
export const DELIVERY_ACCEPTED_EVENT = 'asset.delivery_accepted';
export const PORTAL_UPLOAD_ACCEPTED_EVENT = 'asset.portal_upload_accepted';
export interface IntakeAcceptedPayload {
  intake: true;
  submissionId: string;
  kind: 'delivery' | 'open';
  company: { id: string; name: string };
  portalUser: { id: string; name: string } | null;
  spec: { id: string; name: string } | null;
  uploadRule: { id: string } | null;
  assignmentId: string | null;
  outcome: 'complete' | 'missing_items' | 'asset';
  versionId: string | null;
  items: Array<{ id: string; kind: string; label: string; state: string }>;
}

export const TriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('upload_completed') }),
  z.object({ type: z.literal('file_discovered'), locationId: z.string().uuid().optional() }),
  z.object({ type: z.literal('watch_folder'), watchFolderId: z.string().uuid().optional() }),
  z.object({ type: z.literal('asset_ready') }),
  z.object({ type: z.literal('metadata_changed'), fields: z.array(z.string()).default([]) }),
  /** Started by a person: on an asset (e.g. from its page), on a sequence (from the editor), or either. */
  z.object({ type: z.literal('manual'), subject: z.enum(['any', 'asset', 'sequence']).optional() }),
  z.object({ type: z.literal('schedule'), cron: z.string().min(9).max(120), timezone: z.string().default('UTC') }),
  z.object({ type: z.literal('webhook') }),
  /**
   * Date in metadata (1.0.0-rc.10): for each asset, when its date field ± offsetDays at `time` (or a date-time's own
   * time) in `timezone` comes due. Swept every minute by the API (one replica at a time); each (flow, version, asset,
   * firing time) fires once. `conditions` are checked at firing time. Firing times that had passed when the date was
   * set or the version published fire only with `pastDates`; a firing missed while PRISM was down runs within
   * `catchUpHours`, else it is skipped and logged.
   */
  z.object({
    type: z.literal('metadata_date'), ...DateRuleShape,
    pastDates: z.boolean().default(false),
    catchUpHours: z.number().int().min(0).max(24 * 30).default(24),
    conditions: z.array(ConditionSchema).max(10).default([]),
  }),
  /**
   * Master added (1.2.0): a further master arrived on an asset (upload, watch folder, workflow, check in), optionally
   * only of one variant. The run's trigger payload is the MasterAddedPayload (masters.ts), e.g.
   * trigger.payload.variantId, and {{master.*}} names the new master.
   */
  z.object({ type: z.literal('master_added'), variantId: z.string().uuid().optional() }),
  /**
   * 1.3.0 media portal: a delivery into a placeholder was accepted in Intake (the run is on the placeholder, now with
   * its media), or an open upload was accepted (the run is on the new asset, or the placeholder it was linked to).
   * The domain events (asset.delivery_accepted, asset.portal_upload_accepted) carry the company, the spec or rule
   * and the items.
   */
  z.object({ type: z.literal('delivery_accepted') }),
  z.object({ type: z.literal('portal_upload_accepted') }),
]);
export type Trigger = z.infer<typeof TriggerSchema>;
export type MetadataDateTrigger = Extract<Trigger, { type: 'metadata_date' }>;

export const TRIGGER_LABELS: Record<Trigger['type'], string> = {
  upload_completed: 'Upload completed',
  file_discovered: 'File discovered by an agent',
  watch_folder: 'Ingested from a watch folder',
  asset_ready: 'Asset ready',
  metadata_changed: 'Metadata changed',
  manual: 'Manual run',
  schedule: 'Schedule',
  webhook: 'Authenticated webhook',
  metadata_date: 'Date in metadata',
  master_added: 'Master added',
  delivery_accepted: 'Portal delivery accepted',
  portal_upload_accepted: 'Portal upload accepted',
};

/** Triggers whose runs are on an asset. */
export const ASSET_TRIGGERS: Array<Trigger['type']> = ['upload_completed', 'file_discovered', 'watch_folder', 'asset_ready', 'metadata_changed', 'webhook', 'metadata_date', 'master_added', 'delivery_accepted', 'portal_upload_accepted'];

/** Events that start runs, mapped from domain events. */
export const TRIGGER_EVENTS: Partial<Record<Trigger['type'], string[]>> = {
  upload_completed: ['asset.uploaded'],
  file_discovered: ['asset.discovered'],
  watch_folder: ['asset.watch_ingested'],
  asset_ready: ['asset.ready'],
  metadata_changed: ['asset.metadata_changed'],
  master_added: [MASTER_ADDED_EVENT],
  delivery_accepted: [DELIVERY_ACCEPTED_EVENT],
  portal_upload_accepted: [PORTAL_UPLOAD_ACCEPTED_EVENT],
};

/* ---------------------------------------------------------------- nodes */

const Base = {
  id: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/),
  label: z.string().max(80).optional(),
  position: z.object({ x: z.number(), y: z.number() }),
  timeoutSeconds: z.number().int().positive().max(7 * 24 * 3600).optional(),
  retries: z.number().int().min(0).max(10).optional(),
};

/** `{{path}}` templates resolve against the run context. */
const Templated = z.string().max(2000);

/**
 * A call to a site service through a storage agent's API gateway (1.6.0, gateway.ts): the agent, the service it
 * reports, a method the service allows, and the templated path below the service's base URL (checked against the
 * service's patterns once rendered, by PRISM and again by the agent), query parameters and, for POST, PUT and PATCH,
 * a JSON body of templated strings (payload, as Call webhook's).
 */
const SiteServiceCall = {
  agentId: z.string().uuid(),
  service: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/),
  method: z.enum(GATEWAY_METHODS).default('GET'),
  path: Templated.default('/'),
  query: z.record(z.string().max(200), Templated).default({}),
  payload: z.record(z.string().max(200), Templated).default({}),
};

/** How a For each step (1.6.0) runs its body: how many at a time, at most how many, and what a failed item does. */
const ForEachShape = {
  /** Items run at the same time (default 2, at most 8). */
  concurrency: z.number().int().min(1).max(FOR_EACH_LIMITS.maxConcurrency).default(FOR_EACH_LIMITS.defaultConcurrency),
  /** The most items one run takes (default and at most 500); the rest are left, and the step says so. */
  maxItems: z.number().int().min(1).max(FOR_EACH_LIMITS.maxItems).default(FOR_EACH_LIMITS.maxItems),
  /** continue: a failed item is counted and the others go on; fail: the step fails at the first failed item. */
  onItemFailure: z.enum(['continue', 'fail']).default('continue'),
};

/** One field set by a Map metadata step. */
export const MappingSchema = z.object({
  /** Metadata field id. */
  field: z.string().min(1).max(80),
  /** A template, e.g. {{trigger.payload.programme_title}} or {{nodes.lookup.output.response.genre}}. */
  from: Templated,
  transform: z.enum(['none', 'trim', 'upper', 'lower', 'capitalize', 'number', 'date', 'first_line']).default('none'),
  /** Replace whole values, e.g. SPORT → Sports; anything not listed passes through. */
  lookup: z.record(z.string(), z.string().max(500)).default({}),
  /** Used when the source is empty. */
  fallback: z.string().max(2000).default(''),
  /** overwrite: always set; if_empty: only fill a field that has no value yet (enrich). */
  mode: z.enum(['overwrite', 'if_empty']).default('overwrite'),
});
export type Mapping = z.infer<typeof MappingSchema>;

/** What a Repeat until step checks each time. */
export const RepeatCheckSchema = z.discriminatedUnion('type', [
  /** Read the asset again (metadata, status, technical facts). */
  z.object({ type: z.literal('asset') }),
  /** Call an endpoint (typically GET); its answer is nodes.<id>.output.response. */
  z.object({ type: z.literal('webhook'), endpointId: z.string().uuid(), path: Templated.default(''), payload: z.record(z.string(), Templated).default({}) }),
  /** Only wait (a timed loop). */
  z.object({ type: z.literal('wait') }),
  /**
   * Wait for delivery (1.3.1, Q4): read the state of a media portal assignment of the run's asset, the one an earlier
   * Assign for delivery step made (fromNode) or, without one, the asset's newest assignment. The answer is
   * nodes.<id>.output.response, a DeliveryCheckResponse; the palette's "Wait for delivery" sets the condition
   * response.state equals accepted.
   */
  z.object({ type: z.literal('delivery_accepted'), fromNode: z.string().max(40).optional() }),
  /**
   * Ask a site service (1.6.0, stream W): the same call as a Call site service step, through the storage agent's API
   * gateway; the answer is nodes.<id>.output.response (the service's JSON or text) and .status.
   */
  z.object({ type: z.literal('site_service'), ...SiteServiceCall }),
]);
export type RepeatCheck = z.infer<typeof RepeatCheckSchema>;

/** What a delivery_accepted check answers (nodes.<id>.output.response; 1.3.1). */
export interface DeliveryCheckResponse {
  /** null: the asset has no assignment (yet). */
  assignmentId: string | null;
  /** The assignment's state (ASSIGNMENT_STATES), or 'none'. */
  state: 'none' | 'open' | 'in_progress' | 'in_review' | 'accepted' | 'cancelled' | 'expired';
  outcome: 'complete' | 'missing_items' | null;
  /** state accepted (with any outcome). */
  accepted: boolean;
  /** cancelled or expired: it will never be accepted (the step may leave by false at once; Q4 decides). */
  finished: boolean;
  acceptedAt: string | null;
  dueAt: string | null;
}

/**
 * Who an Assign for delivery step assigns to (1.3.1): every user of a portal company, or one portal user (whose
 * company it is). Both are checked against the workspace when the workflow is saved or published.
 */
export const DeliveryAssigneeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('company'), companyId: z.string().uuid() }),
  z.object({ type: z.literal('user'), portalUserId: z.string().uuid() }),
]);
export type DeliveryAssignee = z.infer<typeof DeliveryAssigneeSchema>;

/** When an Assign for delivery step's assignment is due: none, or days and hours after the step runs (1.3.1). */
export const DeliveryDueSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({
    type: z.literal('after_run'), days: z.number().int().min(0).max(365).default(0), hours: z.number().int().min(0).max(24 * 365).default(0),
    /** The IANA zone the supplier's emails show the due date in (1.3.1); UTC without one. */
    timeZone: z.string().max(64).optional(),
  }),
]);
export type DeliveryDue = z.infer<typeof DeliveryDueSchema>;

/** When a Create task step's task is due (1.0.0-rc.10). */
export const TaskDueSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  /** Relative to now: hours and days after the step runs. */
  z.object({ type: z.literal('now'), days: z.number().int().min(0).max(365).default(0), hours: z.number().int().min(0).max(24 * 365).default(0) }),
  /** Relative to the run's asset's date field, ± days at a time. */
  z.object({ type: z.literal('field'), ...DateRuleShape }),
]);
export type TaskDue = z.infer<typeof TaskDueSchema>;

/**
 * A transcode's result (1.2.0): a rendition (as before), or a master of the asset with a variant (null: the source's
 * variant) and the probe's format. An existing master of that variant and format is replaced (the old file is kept
 * until the new one is verified) or the step skips saving. The same-content check applies (DIFFERENT_CONTENT).
 */
export const SaveAsSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('rendition') }),
  z.object({ type: z.literal('master'), variantId: z.string().uuid().nullable().default(null), onExisting: z.enum(['replace', 'skip']).default('replace') }),
]);
export type SaveAs = z.infer<typeof SaveAsSchema>;

/** Subtitles of an Export asset step: none, burnt in, sidecar files or both, from the default track or a language. */
export const ExportSubtitlesSchema = z.object({
  mode: z.enum(['none', 'burn', 'sidecar', 'both']).default('none'),
  /** A BCP 47 language of one of the asset's tracks (its first such track); null: the asset's default track. */
  language: z.string().min(2).max(35).nullable().default(null),
  /** 1.9.0: the sidecar formats for mode sidecar or both; left out: SRT and WebVTT (stored definitions keep their JSON). */
  sidecarFormats: SidecarFormatsSchema.optional(),
});

const PRIMARY = (): MasterChoice => ({ type: 'primary' });
const PRIMARY_OR_ALL = (): MasterChoiceOrAll => ({ type: 'primary' });

export const NodeSchema = z.discriminatedUnion('type', [
  z.object({ ...Base, type: z.literal('trigger'), trigger: TriggerSchema }),
  z.object({ ...Base, type: z.literal('condition'), all: z.array(ConditionSchema).min(1).max(10) }),
  z.object({ ...Base, type: z.literal('validate_metadata'), required: z.array(z.string()).min(1) }),
  /**
   * 1.6.0 (stream A): asSuggestion writes the values as metadata suggestions to accept or reject (metadata_suggestions,
   * 0100) instead of setting them. Optional, not defaulted, so earlier graphs parse to the same nodes.
   */
  z.object({ ...Base, type: z.literal('update_metadata'), values: z.record(z.string(), Templated), asSuggestion: z.boolean().optional() }),
  z.object({ ...Base, type: z.literal('inspect_media') }),
  z.object({ ...Base, type: z.literal('generate_derivatives') }),
  /** preset: a built-in preset (h264-1080p, h264-720p) or the id of a transcode profile from Administration. */
  /** 1.2.0: source (which master is read; the primary by default) and saveAs (a rendition by default, or a master). */
  z.object({
    ...Base, type: z.literal('transcode'), preset: z.string().min(1).max(64),
    source: MasterChoiceSchema.default(PRIMARY), saveAs: SaveAsSchema.default({ type: 'rendition' }),
  }),
  /** A job on the site's external transcoder (see packages/contracts/src/transcoder.ts); source and saveAs as Transcode's. */
  z.object({
    ...Base, type: z.literal('external_transcode'), profileId: z.string().max(64), labels: z.record(z.string(), Templated).default({}),
    source: MasterChoiceSchema.default(PRIMARY), saveAs: SaveAsSchema.default({ type: 'rendition' }),
  }),
  /**
   * representation 'output': the file an earlier transcode step (fromNode) made. master (1.2.0): with 'original', which
   * master is copied (the primary by default, or every master).
   */
  z.object({
    ...Base, type: z.literal('copy_to_storage'), locationId: z.string().uuid(), representation: z.enum(['original', 'proxy', 'output']), fromNode: z.string().max(40).optional(),
    master: MasterChoiceOrAllSchema.default(PRIMARY_OR_ALL),
  }),
  /**
   * Delete: one copy of a file from a storage location, or the whole asset (to
   * the recycle bin). onlyCopy 'fail' refuses to delete the last copy of a file.
   */
  z.object({
    ...Base, type: z.literal('delete'), target: z.enum(['copy', 'asset']).default('copy'),
    representation: z.enum(['original', 'proxy', 'output']).default('original'), fromNode: z.string().max(40).optional(),
    locationId: z.string().uuid().optional(), onlyCopy: z.enum(['fail', 'allow']).default('fail'),
    /** 1.2.0: with target copy and representation 'original', which master's copy (the primary by default, or every master's). */
    master: MasterChoiceOrAllSchema.default(PRIMARY_OR_ALL),
  }),
  /**
   * Export asset (1.2.0): the run's asset, whole or its programme, in a preset or the original format, read from its
   * original or proxy and from the master chosen (default: Programme, the format closest to the preset), with
   * subtitles, to an export location (or the exports tier). Waits for the export; later steps use {{output.exportId}}
   * and {{output.key}}. Flow exports do not count toward the batch export limit.
   */
  z.object({
    ...Base, type: z.literal('export_asset'), preset: z.enum(EXPORT_FORMAT_IDS),
    quality: z.enum(['original', 'proxy']).default('original'),
    master: MasterChoiceSchema.default({ type: 'variant', variantId: null, format: null }),
    range: z.enum(['full', 'programme']).default('full'),
    subtitles: ExportSubtitlesSchema.default({ mode: 'none', language: null }),
    destinationLocationId: z.string().uuid().optional(),
    /** Restore archived media first; the step waits for it. */
    restore: z.boolean().default(false),
  }),
  /**
   * Without a sequenceId, exports the sequence the run was started on.
   * destinationLocationId: an export location (or the exports tier); omitted, the exports tier.
   */
  z.object({
    ...Base, type: z.literal('export_sequence'), sequenceId: z.string().uuid().optional(), preset: z.enum(EXPORT_PRESET_IDS),
    quality: z.enum(['original', 'proxy']).default('original'), destinationLocationId: z.string().uuid().optional(),
  }),
  /** A CMX 3600 EDL of the run's sequence, stored with the exports. */
  z.object({ ...Base, type: z.literal('export_edl'), title: Templated.default('{{sequence.name}}') }),
  z.object({
    ...Base, type: z.literal('request_approval'),
    /**
     * Who decides: a role key of the workspace (anyone with that role), or (0.31.0) one person, userId.
     * Exactly one is set; both are checked against the workspace when the workflow is saved or published.
     */
    role: z.string().regex(/^[a-z0-9_]{1,40}$/).optional(),
    userId: z.string().uuid().optional(),
    instructions: Templated,
  }),
  /**
   * Call an endpoint from Administration → Webhooks. path is appended to its URL
   * (templated, e.g. items/{{asset.metadata.f_house_id}}); payload is the JSON
   * body, or the query parameters for GET. A JSON answer is available to later
   * steps as nodes.<id>.output.response.
   */
  /** Wait before the next step (durably: survives restarts). */
  z.object({ ...Base, type: z.literal('delay'), seconds: z.number().int().min(1).max(30 * 24 * 3600) }),
  /**
   * A loop with a delay: every everySeconds, up to maxAttempts times, run the
   * check and test the conditions. Leaves by "true" when they hold, "false" when it gives up.
   */
  z.object({
    ...Base, type: z.literal('repeat_until'), everySeconds: z.number().int().min(5).max(24 * 3600), maxAttempts: z.number().int().min(1).max(1000),
    check: RepeatCheckSchema, all: z.array(ConditionSchema).min(1).max(10),
  }),
  /** Set metadata fields from the trigger, earlier steps or a webhook's answer, with transforms and value lookups. */
  z.object({ ...Base, type: z.literal('map_metadata'), mappings: z.array(MappingSchema).min(1).max(50) }),
  /** Start or stop a live source (with a schedule trigger and Delay: record a programme at set times). */
  z.object({ ...Base, type: z.literal('live_source'), sourceId: z.string().uuid(), action: z.enum(['start', 'stop']) }),
  /**
   * 1.6.0 (stream A), all optional so earlier graphs parse to the same nodes:
   *  - bodyJson: the body written as JSON, with templates in string values and {{json path}} for a value as JSON
   *    (parseBodyJson / renderBodyJson, automation-eval.ts); validated when the flow is published. With it, payload
   *    is not sent and the answer may be up to 1 MB (WEBHOOK_JSON_MAX_RESPONSE_BYTES);
   *  - requestTimeoutSeconds: the endpoint's time to answer, 1-120 (default 15);
   *  - mediaLinkTtlSeconds: how long the signed media links of TEMPLATE_FUNCTIONS stay valid (default 1 h, at most
   *    24 h). Links are refused unless the endpoint may receive media links.
   */
  z.object({
    ...Base, type: z.literal('call_webhook'), endpointId: z.string().uuid(), path: Templated.default(''), payload: z.record(z.string(), Templated).default({}),
    bodyJson: z.string().max(100_000).optional(),
    requestTimeoutSeconds: z.number().int().min(1).max(120).optional(),
    mediaLinkTtlSeconds: z.number().int().min(MEDIA_LINK_TTL.minSeconds).max(MEDIA_LINK_TTL.maxSeconds).optional(),
  }),
  /**
   * Run QC (1.0.0-rc.8) with a profile (the workspace's default when none is chosen) on the original, the proxy or the
   * output of an earlier transcode step (fromNode). Leaves by passed, warnings or failed.
   */
  z.object({
    ...Base, type: z.literal('run_qc'), profileId: z.string().uuid().optional(),
    source: z.enum(['original', 'proxy', 'output']).default('original'), fromNode: z.string().max(40).optional(),
    /** 1.2.0: with source 'original', which master is checked (the primary by default). */
    master: MasterChoiceSchema.default(PRIMARY),
  }),
  /**
   * Wait until a date (1.0.0-rc.10): until the run's asset's date field ± days at a time, on a durable timer. On
   * waking it reads the date again and waits again if it changed. An empty date, or one already passed when the step
   * starts, continues at once or fails (ifMissing).
   */
  z.object({ ...Base, type: z.literal('wait_until_date'), ...DateRuleShape, ifMissing: z.enum(['continue', 'fail']).default('continue') }),
  /**
   * Create task (1.0.0-rc.10): a task for a person, a role or an access group, linked to the run's asset (or its
   * sequence), titled and described with templates, due relative to now or to a date field. Members are notified as
   * for any task.
   */
  z.object({
    ...Base, type: z.literal('create_task'), assignee: QcTaskAssigneeSchema,
    title: z.string().min(1).max(500), details: Templated.default(''), due: TaskDueSchema.default({ type: 'none' }),
  }),
  /**
   * Assign for delivery (1.3.1, Q4): a media portal assignment of the run's asset (a placeholder) to a company or one
   * of its users, with a delivery spec (null: none; the spec's default items are requested too), due relative to the
   * run, extra requested items, and notes for the supplier (templated). One per run and step
   * (delivery_assignments.automation_run_id / automation_node_id, 0093). ifOpen: what happens when the asset already
   * has an assignment that is not finished: fail the step (ASSIGNMENT_OPEN), or continue with that one.
   */
  z.object({
    ...Base, type: z.literal('assign_for_delivery'), assignee: DeliveryAssigneeSchema,
    specId: z.string().uuid().nullable().default(null), due: DeliveryDueSchema.default({ type: 'after_run', days: 7, hours: 0 }),
    items: z.array(DeliveryItemTemplate).max(30).default([]), notes: Templated.default(''),
    ifOpen: z.enum(['fail', 'use_existing']).default('fail'),
    /**
     * 1.4.0 (G2 builds the behaviour): what is assigned. Absent or 'asset': the run's asset. 'group': "the run's
     * group", every placeholder of the run's asset's group of type groupTypeId (null: its lowest group, e.g. the
     * Season) and of its sub-groups, as POST /api/v1/assignments {groupId} (the assignments get group_id). Optional,
     * not defaulted, so 1.3.1 graphs parse to the same nodes.
     */
    scope: z.enum(['asset', 'group']).optional(),
    groupTypeId: z.string().uuid().nullable().optional(),
  }),
  /**
   * Detect shots (1.5.0, S1 builds the behaviour): detect the run's asset's shots on its proxy and remake its scrub
   * images and Shots layer, as Detect shots on the asset page (the detectShots workflow, run as a child and waited
   * for). The overrides are optional; missing ones take the workspace's settings. Log layers are never touched.
   */
  z.object({
    ...Base, type: z.literal('detect_shots'),
    sensitivity: z.number().int().min(1).max(100).optional(),
    minShotSeconds: z.number().min(0.2).max(10).optional(),
    keyframeIntervalSeconds: z.number().min(2).max(60).optional(),
  }),
  /**
   * Call site service (1.6.0, stream W): a request to a service on a site, through that site's storage agent's API
   * gateway (an agent job of kind http). Outputs {status, response}, as Call webhook. 2xx continues; 429 and 5xx are
   * retried; other 4xx fail without retry; an offline agent is waited for.
   */
  z.object({ ...Base, type: z.literal('call_site_service'), ...SiteServiceCall }),
  /**
   * For each shot (1.6.0, stream A): runs the steps after its `each` port once per shot of the run's asset's current
   * version (the Shots layer), with {{item.*}} (ForEachItem; also {{shot.*}}), then continues by `next`. The steps
   * reached from `each` are its body (forEachBodies): nothing outside leads into them.
   */
  z.object({ ...Base, type: z.literal('for_each_shot'), ...ForEachShape }),
  /** For each segment of a layer (1.6.0, stream A): as For each shot, over the segments of one log layer (onlyEmpty: those without text or values). */
  z.object({
    ...Base, type: z.literal('for_each_segment'), stratumId: z.string().regex(/^s_[a-z0-9_]{1,40}$/), onlyEmpty: z.boolean().default(false), ...ForEachShape,
  }),
  /**
   * Update segment (1.6.0, stream A): inside a For each body, writes text or values into the current item's segment on
   * a layer (for a shot: the segment with the shot's span, made if missing), always as a suggestion (suggested_by
   * flow:<run>), never over what a person wrote (that item is skipped: SEGMENT_HUMAN).
   */
  z.object({
    ...Base, type: z.literal('update_segment'), stratumId: z.string().regex(/^s_[a-z0-9_]{1,40}$/),
    text: Templated.default(''), values: z.array(Templated).max(50).default([]),
  }),
]);
export type AutomationNode = z.infer<typeof NodeSchema>;
export type NodeType = AutomationNode['type'];

export const EdgeSchema = z.object({
  from: z.string(),
  /** 1.6.0: `each` leads into a For each step's body. */
  port: z.enum(['next', 'true', 'false', 'approved', 'rejected', 'passed', 'warnings', 'failed', 'each']).default('next'),
  to: z.string(),
});
export type AutomationEdge = z.infer<typeof EdgeSchema>;

export const AutomationGraphSchema = z.object({
  schemaVersion: z.literal(AUTOMATION_SCHEMA_VERSION),
  nodes: z.array(NodeSchema).min(1).max(100),
  edges: z.array(EdgeSchema).max(300),
});
export type AutomationGraph = z.infer<typeof AutomationGraphSchema>;

export interface NodeTypeInfo {
  label: string;
  category: 'trigger' | 'logic' | 'metadata' | 'media' | 'delivery' | 'people';
  ports: Array<AutomationEdge['port']>;
  needsAsset: boolean;
  /** Works on the sequence a run was started on. */
  needsSequence?: boolean;
  /**
   * The media of the run's asset the step reads, checked before a manual start (preflight): its original, its proxy,
   * or nothing. Copy to storage takes it from its representation; Export sequence checks the sequence's sources instead.
   */
  needs: 'original' | 'proxy' | 'none';
  /** Output fields available to later nodes (typed mappings); objects hold further fields (e.g. a webhook's JSON answer). */
  outputs: Record<string, 'string' | 'number' | 'boolean' | 'object'>;
  description: string;
}

export const NODE_TYPES: Record<NodeType, NodeTypeInfo> = {
  trigger: { label: 'Trigger', category: 'trigger', ports: ['next'], needsAsset: false, needs: 'none', outputs: {}, description: 'What starts the workflow' },
  condition: { label: 'Condition', category: 'logic', ports: ['true', 'false'], needsAsset: false, needs: 'none', outputs: {}, description: 'Branch on metadata, media facts or earlier results' },
  validate_metadata: { label: 'Validate metadata', category: 'metadata', ports: ['next'], needsAsset: true, needs: 'none', outputs: { valid: 'boolean', missing: 'string' }, description: 'Fail the run if required fields are empty' },
  update_metadata: { label: 'Update metadata', category: 'metadata', ports: ['next'], needsAsset: true, needs: 'none', outputs: { changed: 'number' }, description: 'Set fields from values or templates' },
  inspect_media: { label: 'Inspect media', category: 'media', ports: ['next'], needsAsset: true, needs: 'original', outputs: { durationSeconds: 'number', mediaKind: 'string', width: 'number', height: 'number', frameRate: 'string' }, description: 'Read technical facts with ffprobe' },
  generate_derivatives: { label: 'Generate derivatives', category: 'media', ports: ['next'], needsAsset: true, needs: 'original', outputs: { status: 'string' }, description: 'Re-run proxy, poster, sprite and waveform generation' },
  transcode: { label: 'Transcode', category: 'media', ports: ['next'], needsAsset: true, needs: 'original', outputs: { representationId: 'string', sizeBytes: 'number', filename: 'string', key: 'string', profile: 'string' }, description: 'Make a rendition of the original with a transcode profile; later steps can copy or delete it as “Output of this step”' },
  external_transcode: { label: 'External transcode', category: 'media', ports: ['next'], needsAsset: true, needs: 'original', outputs: { representationId: 'string', sizeBytes: 'number', externalJobId: 'string' }, description: 'Run a profile on the site’s external transcoder' },
  copy_to_storage: { label: 'Copy to storage', category: 'delivery', ports: ['next'], needsAsset: true, needs: 'original', outputs: { transferId: 'string', bytes: 'number' }, description: 'Verified copy of the original, the proxy or a transcode output to another location' },
  delete: { label: 'Delete', category: 'delivery', ports: ['next'], needsAsset: true, needs: 'none', outputs: { deleted: 'number' }, description: 'Delete a copy of a file from a location (refusing the last copy unless allowed), or move the asset to the recycle bin' },
  export_asset: {
    label: 'Export asset', category: 'delivery', ports: ['next'], needsAsset: true, needs: 'original',
    outputs: { exportId: 'string', key: 'string', sizeBytes: 'number' },
    description: 'Export the asset, whole or its programme, in a format preset or its original format, from the master chosen, to an export location',
  },
  export_sequence: { label: 'Export sequence', category: 'delivery', ports: ['next'], needsAsset: false, needs: 'none', outputs: { exportId: 'string', sizeBytes: 'number' }, description: 'Render a sequence (a chosen one, or the one the run started on) with full lineage' },
  export_edl: { label: 'Export EDL', category: 'delivery', ports: ['next'], needsAsset: false, needsSequence: true, needs: 'none', outputs: { events: 'number', key: 'string' }, description: 'Write a CMX 3600 EDL of the sequence the run started on' },
  request_approval: { label: 'Request approval', category: 'people', ports: ['approved', 'rejected'], needsAsset: false, needs: 'none', outputs: { decidedBy: 'string', comment: 'string' }, description: 'Wait for a person to approve or reject' },
  delay: { label: 'Delay', category: 'logic', ports: ['next'], needsAsset: false, needs: 'none', outputs: {}, description: 'Wait a while before the next step' },
  repeat_until: { label: 'Repeat until', category: 'logic', ports: ['true', 'false'], needsAsset: false, needs: 'none', outputs: { attempts: 'number', met: 'boolean', response: 'object' }, description: 'Check again every so often (the asset, an endpoint, or just time) until conditions hold or it gives up' },
  map_metadata: { label: 'Map metadata', category: 'metadata', ports: ['next'], needsAsset: true, needs: 'none', outputs: { changed: 'number', skipped: 'number' }, description: 'Fill fields from the trigger, earlier steps or a webhook’s answer, with transforms and lookups; overwrite or only fill empty fields' },
  live_source: { label: 'Live source', category: 'media', ports: ['next'], needsAsset: false, needs: 'none', outputs: { armed: 'boolean' }, description: 'Start or stop an RTMP or SRT live source; with a schedule trigger and a Delay it records at set times' },
  call_webhook: { label: 'Call webhook', category: 'delivery', ports: ['next'], needsAsset: false, needs: 'none', outputs: { status: 'number', response: 'object' }, description: 'Call an endpoint from Administration → Webhooks (POST, PUT or GET), signed; later steps can use its JSON answer' },
  run_qc: {
    label: 'Run QC', category: 'media', ports: ['passed', 'warnings', 'failed'], needsAsset: true, needs: 'original',
    outputs: { status: 'string', errors: 'number', warnings: 'number', loudnessLufs: 'number', truePeakDbtp: 'number', runId: 'string' },
    description: 'Check the original, the proxy or a transcode output against a QC profile; continue by passed, warnings or failed',
  },
  wait_until_date: {
    label: 'Wait until a date', category: 'logic', ports: ['next'], needsAsset: true, needs: 'none', outputs: { waitedUntil: 'string', date: 'string', waited: 'boolean' },
    description: 'Wait until a date or date-time field of the asset ± days at a time; a changed date is waited for again',
  },
  create_task: {
    label: 'Create task', category: 'people', ports: ['next'], needsAsset: false, needs: 'none', outputs: { taskId: 'string', dueAt: 'string' },
    description: 'Create a task for a person, a role or an access group, linked to the run’s asset or sequence, due relative to now or to a date field',
  },
  assign_for_delivery: {
    label: 'Assign for delivery', category: 'people', ports: ['next'], needsAsset: true, needs: 'none',
    outputs: { assignmentId: 'string', dueAt: 'string', company: 'string', existing: 'boolean', state: 'string' },
    description: 'Ask a production company (or one of its users) to deliver the asset through the media portal, with a delivery spec, a due date and extra items',
  },
  detect_shots: {
    label: 'Detect shots', category: 'media', ports: ['next'], needsAsset: true, needs: 'proxy',
    outputs: { shots: 'number', tiles: 'number', detected: 'boolean' },
    description: 'Detect the asset’s shots on its proxy and remake its scrub images and Shots layer (log layers are left as they are)',
  },
  call_site_service: {
    label: 'Call site service', category: 'delivery', ports: ['next'], needsAsset: false, needs: 'none', outputs: { status: 'number', response: 'object' },
    description: 'Call a service on a site through its storage agent’s API gateway (services the site lists in gateway.json); later steps can use its answer',
  },
  for_each_shot: {
    label: 'For each shot', category: 'logic', ports: ['each', 'next'], needsAsset: true, needs: 'none', outputs: { items: 'number', succeeded: 'number', failed: 'number', skipped: 'number', calls: 'number' },
    description: 'Run the steps after “each” once per shot of the asset, a few at a time, then continue',
  },
  for_each_segment: {
    label: 'For each segment', category: 'logic', ports: ['each', 'next'], needsAsset: true, needs: 'none', outputs: { items: 'number', succeeded: 'number', failed: 'number', skipped: 'number', calls: 'number' },
    description: 'Run the steps after “each” once per segment of a log layer, a few at a time, then continue',
  },
  update_segment: {
    label: 'Update segment', category: 'metadata', ports: ['next'], needsAsset: true, needs: 'none', outputs: { segmentId: 'string', created: 'boolean', skipped: 'boolean' },
    description: 'Write text or values into the current shot’s or segment’s segment on a log layer, as a suggestion to accept (never over what a person wrote)',
  },
};

/** The date fields a node reads (the trigger's, Wait until a date's, a Create task's due date). */
export function dateFieldsOf(n: AutomationNode): string[] {
  if (n.type === 'trigger' && n.trigger.type === 'metadata_date') return [n.trigger.fieldId];
  if (n.type === 'wait_until_date') return [n.fieldId];
  if (n.type === 'create_task' && n.due.type === 'field') return [n.due.fieldId];
  return [];
}

/* ---------------------------------------------------------------- validation */

export interface GraphProblem {
  nodeId?: string;
  message: string;
}

export function triggerOf(graph: AutomationGraph): Extract<AutomationNode, { type: 'trigger' }> | null {
  return (graph.nodes.find((n) => n.type === 'trigger') as Extract<AutomationNode, { type: 'trigger' }> | undefined) ?? null;
}

/** Structural checks: one trigger, known ports, no cycles, everything reachable, asset nodes need an asset trigger. */
export function checkGraph(graph: AutomationGraph): GraphProblem[] {
  const problems: GraphProblem[] = [];
  const ids = new Set<string>();
  for (const n of graph.nodes) {
    if (ids.has(n.id)) problems.push({ nodeId: n.id, message: `Duplicate node id ${n.id}` });
    ids.add(n.id);
  }
  const triggers = graph.nodes.filter((n) => n.type === 'trigger');
  if (triggers.length !== 1) problems.push({ message: 'A workflow needs exactly one trigger' });
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  for (const e of graph.edges) {
    const from = byId.get(e.from);
    const to = byId.get(e.to);
    if (!from || !to) { problems.push({ message: `Edge ${e.from} → ${e.to} references a missing node` }); continue; }
    if (!NODE_TYPES[from.type].ports.includes(e.port)) problems.push({ nodeId: from.id, message: `${from.label ?? from.id} has no “${e.port}” output` });
    if (to.type === 'trigger') problems.push({ nodeId: to.id, message: 'Nothing can lead into the trigger' });
    if (e.from === e.to) problems.push({ nodeId: e.from, message: 'A node cannot connect to itself' });
  }
  // Cycle detection (Kahn): workflows must be acyclic so every run terminates.
  const indeg = new Map([...ids].map((id) => [id, 0]));
  for (const e of graph.edges) if (indeg.has(e.to)) indeg.set(e.to, indeg.get(e.to)! + 1);
  const queue = [...indeg].filter(([, d]) => d === 0).map(([id]) => id);
  let seen = 0;
  while (queue.length) {
    const id = queue.shift()!;
    seen++;
    for (const e of graph.edges.filter((x) => x.from === id)) {
      indeg.set(e.to, indeg.get(e.to)! - 1);
      if (indeg.get(e.to) === 0) queue.push(e.to);
    }
  }
  if (seen !== ids.size) problems.push({ message: 'The workflow contains a loop. Workflows must flow in one direction so every run finishes.' });
  // Reachability from the trigger.
  const trigger = triggers[0];
  if (trigger) {
    const reach = new Set([trigger.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const e of graph.edges) if (reach.has(e.from) && !reach.has(e.to)) { reach.add(e.to); grew = true; }
    }
    for (const n of graph.nodes) if (!reach.has(n.id)) problems.push({ nodeId: n.id, message: `${n.label ?? NODE_TYPES[n.type].label} is not connected to the trigger` });
    const t = trigger.trigger;
    // A webhook caller may pass an assetId; steps that need one fail clearly when it does not.
    const assetTrigger = ASSET_TRIGGERS.includes(t.type) || (t.type === 'manual' && t.subject !== 'sequence');
    const sequenceTrigger = t.type === 'manual' && t.subject !== 'asset';
    // "Output of a step" must name a transcode step that runs before this one.
    const upstream = (id: string) => {
      const seenUp = new Set<string>();
      const stack = [id];
      while (stack.length) { const cur = stack.pop(); for (const e of graph.edges.filter((x) => x.to === cur)) if (!seenUp.has(e.from)) { seenUp.add(e.from); stack.push(e.from); } }
      return seenUp;
    };
    for (const n of graph.nodes) {
      if ((n.type === 'copy_to_storage' || n.type === 'delete') && n.representation === 'output') {
        const src = n.fromNode ? byId.get(n.fromNode) : undefined;
        if (!src || !['transcode', 'external_transcode'].includes(src.type)) problems.push({ nodeId: n.id, message: `${n.label ?? NODE_TYPES[n.type].label}: choose the transcode step whose output to use` });
        else if (!upstream(n.id).has(src.id)) problems.push({ nodeId: n.id, message: `${n.label ?? NODE_TYPES[n.type].label} must come after “${src.label ?? NODE_TYPES[src.type].label}”, whose output it uses` });
      }
      if (n.type === 'run_qc' && n.source === 'output') {
        // The same "Output of this step" rule as copy and delete; an external transcode's output is checked like any rendition.
        const src = n.fromNode ? byId.get(n.fromNode) : undefined;
        if (!src || !['transcode', 'external_transcode'].includes(src.type)) problems.push({ nodeId: n.id, message: `${n.label ?? NODE_TYPES[n.type].label}: choose the transcode step whose output to check` });
        else if (!upstream(n.id).has(src.id)) problems.push({ nodeId: n.id, message: `${n.label ?? NODE_TYPES[n.type].label} must come after “${src.label ?? NODE_TYPES[src.type].label}”, whose output it checks` });
      }
      if (n.type === 'repeat_until' && n.check.type === 'asset' && !assetTrigger) problems.push({ nodeId: n.id, message: `${n.label ?? 'Repeat until'} checks the asset; use an asset trigger or a manual run on an asset` });
      // Wait for delivery (1.3.1): the asset's assignment, or the one an earlier Assign for delivery step made.
      if (n.type === 'repeat_until' && n.check.type === 'delivery_accepted') {
        if (!assetTrigger) problems.push({ nodeId: n.id, message: `${n.label ?? 'Wait for delivery'} checks the asset’s delivery; use an asset trigger or a manual run on an asset` });
        const src = n.check.fromNode ? byId.get(n.check.fromNode) : undefined;
        if (n.check.fromNode && src?.type !== 'assign_for_delivery') problems.push({ nodeId: n.id, message: `${n.label ?? 'Wait for delivery'}: choose the Assign for delivery step whose delivery to wait for` });
        else if (src && !upstream(n.id).has(src.id)) problems.push({ nodeId: n.id, message: `${n.label ?? 'Wait for delivery'} must come after “${src.label ?? NODE_TYPES[src.type].label}”, whose delivery it waits for` });
      }
      if (n.type === 'map_metadata') for (const m of n.mappings) if (!m.field) problems.push({ nodeId: n.id, message: `${n.label ?? 'Map metadata'}: choose a field for every mapping` });
      if (n.type === 'delete' && n.target === 'copy' && !n.locationId) problems.push({ nodeId: n.id, message: `${n.label ?? 'Delete'}: choose the storage location to delete from` });
      if (n.type === 'request_approval' && Boolean(n.role) === Boolean(n.userId)) problems.push({ nodeId: n.id, message: `${n.label ?? 'Request approval'}: choose a role or a person who approves` });
      if (n.type === 'create_task' && n.due.type === 'field' && !assetTrigger) problems.push({ nodeId: n.id, message: `${n.label ?? 'Create task'} is due from a date field of the asset; use an asset trigger or a manual run on an asset` });
      if (n.type === 'create_task' && !n.title.trim()) problems.push({ nodeId: n.id, message: `${n.label ?? 'Create task'}: give the task a title` });
      // 1.2.0: a run is on a different asset each time, so a step cannot name one master by id.
      const choice = nodeMasterChoice(n);
      if (choice?.type === 'master') problems.push({ nodeId: n.id, message: `${n.label ?? NODE_TYPES[n.type].label}: choose the primary master or a variant (a workflow cannot name one asset’s master)` });
      if (n.type === 'export_asset' && n.preset === 'original' && (n.subtitles.mode === 'burn' || n.subtitles.mode === 'both')) {
        problems.push({ nodeId: n.id, message: `${n.label ?? 'Export asset'}: burning subtitles in needs a format preset; choose one, or deliver them as sidecar files` });
      }
      if (n.type === 'export_asset' && n.preset === 'original' && n.quality === 'proxy') {
        problems.push({ nodeId: n.id, message: `${n.label ?? 'Export asset'}: the original format is read from the original, not the proxy` });
      }
      if (n.type === 'condition') {
        for (const c of n.all) if ((c.op === 'has_master' || c.op === 'no_master') && !assetTrigger) problems.push({ nodeId: n.id, message: `${n.label ?? 'Condition'} checks the asset’s masters; use an asset trigger or a manual run on an asset` });
      }
    }
    problems.push(...forEachProblems(graph));
    for (const n of graph.nodes) {
      if (NODE_TYPES[n.type].needsAsset && !assetTrigger) problems.push({ nodeId: n.id, message: `${NODE_TYPES[n.type].label} needs an asset; use an asset trigger or a manual run on an asset` });
      if (nodeNeedsSequence(n) && !sequenceTrigger) problems.push({ nodeId: n.id, message: `${NODE_TYPES[n.type].label} needs a sequence; start the workflow manually on a sequence, or choose one in the step` });
    }
  }
  return problems;
}

/** Steps that may not run inside a For each body (1.6.0). */
const NOT_IN_FOR_EACH: ReadonlyArray<NodeType> = ['trigger', 'for_each_shot', 'for_each_segment', 'request_approval', 'assign_for_delivery', 'wait_until_date', 'export_sequence', 'export_edl', 'live_source'];

/**
 * For each (1.6.0): the steps reached from a For each step's `each` port are its body. Nothing outside the body leads
 * into it, bodies are not nested and do not overlap, some steps cannot be in one (NOT_IN_FOR_EACH), only For each steps
 * have an `each` output, and Update segment is only ever in a body.
 */
export function forEachProblems(graph: AutomationGraph): GraphProblem[] {
  const problems: GraphProblem[] = [];
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const bodies = forEachBodies(graph);
  const owner = new Map<string, string>();
  for (const [loopId, body] of bodies) {
    const loop = byId.get(loopId)!;
    const name = loop.label ?? NODE_TYPES[loop.type].label;
    if (!body.size) problems.push({ nodeId: loopId, message: `${name}: connect the steps to run for each item to its “each” output` });
    if (body.has(loopId)) problems.push({ nodeId: loopId, message: `${name} cannot lead back into itself` });
    for (const id of body) {
      const other = owner.get(id);
      if (other && other !== loopId) problems.push({ nodeId: id, message: `${byId.get(id)?.label ?? id} is inside two For each steps` });
      owner.set(id, loopId);
      const n = byId.get(id);
      if (n && NOT_IN_FOR_EACH.includes(n.type)) problems.push({ nodeId: id, message: `${n.label ?? NODE_TYPES[n.type].label} cannot run inside ${name}` });
    }
    for (const e of graph.edges) {
      if (!body.has(e.to)) continue;
      const fromInside = body.has(e.from) || (e.from === loopId && e.port === 'each');
      if (!fromInside) problems.push({ nodeId: e.to, message: `${byId.get(e.to)?.label ?? e.to} is inside ${name}; only its “each” output and the steps inside it may lead there` });
    }
  }
  for (const n of graph.nodes) {
    if (n.type === 'update_segment' && !owner.has(n.id)) problems.push({ nodeId: n.id, message: `${n.label ?? 'Update segment'} works on the current shot or segment: put it inside a For each step` });
  }
  return problems;
}

/** Whether a node's templates use a signed media link (TEMPLATE_FUNCTIONS with link: true); such a node needs an endpoint that may receive them. */
export function nodeUsesMediaLinks(n: AutomationNode): boolean {
  if (n.type !== 'call_webhook') return false;
  return [n.path, n.bodyJson ?? '', ...Object.values(n.payload)].some(isLinkTemplateUsed);
}

/**
 * Date fields and time zones (1.0.0-rc.10): every date field a node names is a date or date-time field of the model,
 * "its own time" is only for date-time fields, and the time zone is known.
 */
export function dateRuleProblems(graph: AutomationGraph, fields: Array<{ id: string; label: string; type: string }>): GraphProblem[] {
  const problems: GraphProblem[] = [];
  for (const n of graph.nodes) {
    const rule = n.type === 'trigger' && n.trigger.type === 'metadata_date' ? n.trigger : n.type === 'wait_until_date' ? n : n.type === 'create_task' && n.due.type === 'field' ? n.due : null;
    if (!rule) continue;
    const name = n.type === 'trigger' ? 'Date in metadata' : n.label ?? NODE_TYPES[n.type].label;
    const f = fields.find((x) => x.id === rule.fieldId);
    if (!f) problems.push({ nodeId: n.id, message: `${name}: choose a date field of the metadata model (${rule.fieldId} is not one)` });
    else if (f.type !== 'date' && f.type !== 'datetime') problems.push({ nodeId: n.id, message: `${name}: “${f.label}” is not a date or date-time field` });
    else if (rule.useFieldTime && f.type !== 'datetime') problems.push({ nodeId: n.id, message: `${name}: “${f.label}” is a date without a time; choose a time of day` });
    if (!isTimeZone(rule.timezone)) problems.push({ nodeId: n.id, message: `${name}: “${rule.timezone}” is not a time zone (use a name such as Europe/Helsinki)` });
  }
  return problems;
}

/** Whether a node works on the sequence the run was started on. */
/** What media of the run's asset this step reads (copy to storage: its representation; a transcode's output is made by the run). */
export function nodeMediaNeed(n: AutomationNode): 'original' | 'proxy' | 'none' {
  if (n.type === 'copy_to_storage') return n.representation === 'output' ? 'none' : n.representation;
  if (n.type === 'run_qc') return n.source === 'output' ? 'none' : n.source;
  if (n.type === 'export_asset') return n.quality;
  return NODE_TYPES[n.type].needs;
}

/**
 * Which master a node reads or acts on (1.2.0), for preflight and the run: Transcode's and External transcode's
 * source, Copy to storage's and Delete's master (with representation 'original'), Run QC's (with source 'original')
 * and Export asset's (with quality 'original'); null when the node reads no master.
 */
export function nodeMasterChoice(n: AutomationNode): MasterChoiceOrAll | null {
  if (n.type === 'transcode' || n.type === 'external_transcode') return n.source;
  if (n.type === 'copy_to_storage') return n.representation === 'original' ? n.master : null;
  if (n.type === 'delete') return n.target === 'copy' && n.representation === 'original' ? n.master : null;
  if (n.type === 'run_qc') return n.source === 'original' ? n.master : null;
  if (n.type === 'export_asset') return n.quality === 'original' ? n.master : null;
  return null;
}

/** The variants a graph names (trigger filter, master choices, save as master, conditions), to check they exist. */
export function variantIdsOf(graph: AutomationGraph): string[] {
  const ids = new Set<string>();
  for (const n of graph.nodes) {
    if (n.type === 'trigger' && n.trigger.type === 'master_added' && n.trigger.variantId) ids.add(n.trigger.variantId);
    const c = nodeMasterChoice(n);
    if (c?.type === 'variant' && c.variantId) ids.add(c.variantId);
    if ((n.type === 'transcode' || n.type === 'external_transcode') && n.saveAs.type === 'master' && n.saveAs.variantId) ids.add(n.saveAs.variantId);
    if (n.type === 'condition') for (const k of n.all) if (k.master?.variantId) ids.add(k.master.variantId);
  }
  return [...ids];
}

/** Variants a graph names that the workspace does not have (1.2.0; checked when a workflow is saved or published). */
export function variantProblems(graph: AutomationGraph, variants: Array<{ id: string }>): GraphProblem[] {
  const known = new Set(variants.map((v) => v.id));
  const problems: GraphProblem[] = [];
  for (const n of graph.nodes) {
    const name = n.type === 'trigger' ? 'Master added' : n.label ?? NODE_TYPES[n.type].label;
    const ids = variantIdsOf({ ...graph, nodes: [n] });
    if (ids.some((id) => !known.has(id))) problems.push({ nodeId: n.id, message: `${name}: choose a master variant from Administration → Metadata model → Master variants (one it names was deleted)` });
  }
  return problems;
}

export function nodeNeedsSequence(n: AutomationNode): boolean {
  return Boolean(NODE_TYPES[n.type].needsSequence) || (n.type === 'export_sequence' && !n.sequenceId);
}

/** What a manual run of this graph needs to be started on. */
export function runSubject(graph: AutomationGraph): { asset: 'required' | 'optional' | 'none'; sequence: 'required' | 'optional' | 'none' } {
  const t = triggerOf(graph)?.trigger;
  const subject = t?.type === 'manual' ? t.subject ?? 'any' : 'any';
  const needsAsset = graph.nodes.some((n) => NODE_TYPES[n.type].needsAsset);
  const needsSequence = graph.nodes.some(nodeNeedsSequence);
  return {
    asset: subject === 'sequence' ? 'none' : needsAsset || subject === 'asset' ? 'required' : 'optional',
    sequence: subject === 'asset' ? 'none' : needsSequence || subject === 'sequence' ? 'required' : 'optional',
  };
}

/* ---------------------------------------------------------------- webhook endpoints */

export interface WebhookEndpointDTO {
  id: string;
  name: string;
  url: string;
  method: 'POST' | 'GET' | 'PUT';
  description: string;
  enabled: boolean;
  /** Names of the extra headers sent with each call (values are never shown). */
  headerNames: string[];
  createdAt: string;
  lastCalledAt: string | null;
  lastStatus: number | null;
  lastError: string | null;
  /** Workflows whose steps call this endpoint. */
  usedBy: string[];
  /**
   * 1.6.0: whether flows may send this endpoint signed media links (TEMPLATE_FUNCTIONS: shotFrameUrl, proxyUrl, …,
   * base64Frame). Off by default; a link to an endpoint without it is refused (MEDIA_LINKS_NOT_ALLOWED).
   */
  mayReceiveMediaLinks: boolean;
}

/* ---------------------------------------------------------------- run DTOs */

export type AutomationRunStatus = 'queued' | 'waiting_resources' | 'running' | 'waiting_approval' | 'retrying' | 'succeeded' | 'failed' | 'cancelled';
export type NodeRunStatus = 'pending' | 'running' | 'retrying' | 'waiting_approval' | 'succeeded' | 'failed' | 'skipped' | 'cancelled';

export interface NodeRunDTO {
  nodeId: string;
  status: NodeRunStatus;
  attempts: number;
  startedAt: string | null;
  finishedAt: string | null;
  output: Record<string, unknown> | null;
  error: { code: string; message: string; retryable: boolean } | null;
  logs: Array<{ at: string; level: 'info' | 'warn' | 'error'; message: string }>;
}

export interface AutomationRunDTO {
  id: string;
  definitionId: string;
  definitionName: string;
  version: number;
  status: AutomationRunStatus;
  trigger: {
    type: Trigger['type']; eventId?: string; assetId?: string | null; assetTitle?: string | null; sequenceId?: string | null; sequenceName?: string | null; actor?: string | null;
    /** Date in metadata (1.0.0-rc.10): the field, its value, when it was due, and whether the run was a catch-up. */
    date?: { fieldId: string; date: string; firingAt: string; caughtUp: boolean; pastDate: boolean } | null;
  };
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: { code: string; message: string; retryable: boolean } | null;
  workflowId: string;
  nodes: NodeRunDTO[];
}

/** "Show the next 20 firings" (1.0.0-rc.10): the next firings of a Date in metadata trigger, earliest first. */
export interface DateFiringPreviewDTO {
  items: Array<{
    assetId: string; assetTitle: string; date: string; firingAt: string;
    /** The trigger's conditions hold now (they are checked again when it fires). */
    conditionsMet: boolean;
  }>;
  /** With "also for dates already passed": dates already passed that would still fire once (not fired before). */
  pastDue: number;
  timezone: string;
}
